import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { CssIdentifierRegex } from '@portal-core/project-files/constants/css-identifier-regexes.constants';
import { FlareSpecialCharsRegex } from '@portal-core/project-files/constants/file-regexes.constants';

export class McValidators {
  // Email validation pattern: x@x.xx
  static MadCapSubscriptionEmailValidator: ValidatorFn = Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

  // must start and end with an alphanumeric character
  // can only contain alphanumeric, underscore, hyphen, plus or forward slash characters
  // cannot contain consecutive underscore, hyphen, plus or forward slash characters
  static BranchNameValidator: ValidatorFn = Validators.pattern(/^(?!.*([\-_\/+])\1)[a-zA-Z0-9]([a-zA-Z0-9_\-\/+]*[a-zA-Z0-9])?$/);

  /**
   * According to gitlab: branch names with 40+ hexadecimal characters are prohibited, because they are similar to Git commit hashes.
  */
  static GitLabBranchNameValidator(control: AbstractControl): { gitlab_branch_hash: boolean } {
    if (!control.value) {
      return null;
    }
    const gitCommitHashesRegex = new RegExp("[\\da-f]{40,}")
    const isInvalid = gitCommitHashesRegex.test(control.value);
    if (isInvalid) {
      return { gitlab_branch_hash: true };
    }
    return null;
  }

  static supportedExternalMultimediaLink(control: AbstractControl): { pattern: boolean } {
    const vimeoPattern = "(?:player.)?vimeo\.com/(?:.*#|.*/)?([0-9]+)";
    const youTubePattern = "youtu(?:\.be|be\.com)/(?:(?:.*)v(?:/|=)|(?:.*/)?)([a-zA-Z0-9-_]+)";
    if (!control.value) {
      return null;
    }
    if (!control.value.match(vimeoPattern) && !control.value.match(youTubePattern)) {
      return { pattern: true };
    }
  }

  static notOnlyWhitespace(control: AbstractControl): { whitespace: boolean } {
    // Required checks this
    if (!control.value) {
      return null;
    }
    const isWhitespace = control.value.trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }

  static validFilePath(control: AbstractControl): { filePath: boolean } {
    let isValid: boolean;
    const lastIndexOfSlash = control.value.lastIndexOf('/');

    if (lastIndexOfSlash === -1 || (lastIndexOfSlash >= 0 && lastIndexOfSlash + 1 !== control.value.length)) {
      isValid = true;
    } else if (lastIndexOfSlash >= 0) {
      isValid = false;
    }

    return isValid ? null : { 'filePath': true };
  }

  static validFileNames(control: AbstractControl): { special_chars: boolean } {
    const files = control.value as File[];
    if (!Array.isArray(files)) {
      return null;
    }
    const specialCharRegex = new RegExp(FlareSpecialCharsRegex)
    const isInvalid = files.some(file => !specialCharRegex.test(file.name));
    return isInvalid ? { special_chars: true } : null;
  }

  static notOnlyWhiteSpaceFileName(control: AbstractControl): { whitespaceFileName: boolean } {
    let isValid: boolean;
    const testValueSplit = control.value.split('/');
    let fileName = testValueSplit.pop();
    if (fileName?.trim().length) {
      isValid = true;
    } else {
      isValid = false;
    }

    return isValid ? null : { 'whitespaceFileName': true };
  }

  static cssIdentifier(control: AbstractControl): { css_identifier: boolean } {
    if (control.value === '') {
      return null;
    }
    const cssIdentifierRegex = new RegExp(CssIdentifierRegex);
    const isInvalid = !cssIdentifierRegex.test(control.value);
    return isInvalid ? { css_identifier: true } : null;
  }

  static validContentSecurityPolicyDirectiveValue(control: AbstractControl): { valid: boolean } | null {
    // for more information visit: https://www.w3.org/TR/CSP3/#framework-directive-source-list.
    const schemePart = '([A-Za-z][A-Za-z0-9+-.]*)';
    const schemeSource = `^(${schemePart}:)$`;
    const hostChar = '[A-Za-z0-9-]';
    const hostPart = `((((\\*\\.)?${hostChar}+)(\\.${hostChar}+)*)|\\*)`;
    const portPart = '([0-9]+|\\*)';
    const pathChars = '[A-Za-z0-9-._~!$&\'()*+=%:@]';
    const pathPart = `((/${pathChars}*)+)`;
    const hostSource = `^((${schemePart}://)?${hostPart}(:${portPart})?${pathPart}?)$`;
    const hostSourceRegEx = new RegExp(hostSource);
    const schemeSourceRegEx = new RegExp(schemeSource);
    const isValid = hostSourceRegEx.test(control.value) || schemeSourceRegEx.test(control.value);
    if (!isValid && control.value.length !== 0) {
      return { valid: true };
    }
    return null;
  }

  static validDomainName(control: AbstractControl): { domain_name: boolean } {
    if (control.value === '') {
      return null;
    }

    // (?=.{1,253}$) - full domain max length
    // (?!-) - cannot start with hyphen
    // [a-zA-Z0-9-]{0,61} - label body
    // (?:label\.)+ - one or more dot separated labels
    // [A-Za-z]{2,63} - top level domain rule
    const regex = new RegExp(/^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[A-Za-z]{2,63}$/);
    const isValid = regex.test(control.value);
    return isValid ? null : { domain_name: true };
  }

  static validURLName(control: AbstractControl): { url: boolean } {
    if (!control.value?.length) {
      return null
    } else {
      // https? - protocol: matches "http" or "https" (the "s" is optional)
      // :\/\/ - literal "://"
      // (www\.)? - optional "www."
      // [a-zA-Z0-9-]+ - domain or subdomain label (letters, numbers, hyphens)
      // (\.[a-zA-Z0-9-]+)+ - one or more ".label" parts (enforces at least one dot, allows subdomains)
      // (:\d+)?  - optional port number (colon followed by digits, e.g. :3000)
      // (\/[^\s]*)? - optional path, query, or fragment starting with "/" (no whitespace allowed)
      const regex = new RegExp(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/);
      const isValid = regex.test(control.value);
      return isValid ? null : { url: true };
    }
  }
}

